import { Component, ElementRef, inject, Injector, OnDestroy, OnInit, signal, Signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { Tab } from '@modules/common/types/tab';
import { isCreationAllowedInVirtualFolder } from '@modules/common/utils/virtual-folder';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { createAndOpenDraft, importAndOpenWorkbook, type NewWorkbookDeps } from '@modules/sheets/new-workbook';
import { fill, SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { TitleBySheetsFilterPipe } from '@modules/sheets/pipes/title-by-sheets-filter.pipe';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SheetFolderListStateService } from '@modules/sheets/services/sheet-folder-list-state.service';
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsListStateService } from '@modules/sheets/services/sheets-list-state.service';
import { openWorkbookInEditor } from '@modules/sheets/open-workbook';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { SheetsAppStateService, WorkbookPosition } from '@modules/sheets/services/state.service';
import { type ListState } from '@modules/sheets/types/list-state';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { SheetFoldersFilters } from '@modules/sheets/types/sheet-folders-filters';
import { SheetsFilters } from '@modules/sheets/types/sheets-filters';
import { VirtualFolder } from '@modules/sheets/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';
import { combineLatest, firstValueFrom, from, of, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';

import { SheetsPanelWorkbookComponent } from './sheets-panel-workbook.component';

/** The dynamic panel's own split key (`DynamicPanelComponent.splitViewKey`). */
const DP_SPLIT_KEY = 'dp';

@Component({
  selector: 'app-sheets-panel',
  templateUrl: './sheets-panel.component.html',
  styleUrls: ['./sheets-panel.component.less'],
  standalone: false,
  providers: [
    ...SheetsListStateService.providers({
      stateKey: StateKey.sheetsDPListState,
      withAdvancedSearch: true,
    }),
    ...SheetFolderListStateService.providers({
      stateKey: StateKey.sheetFoldersDPListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class SheetsPanelComponent implements OnInit, OnDestroy {
  public sheetFoldersFilters: SheetFoldersFilters;
  public sheetsFilters: SheetsFilters;
  public contextMenuOpened: boolean;
  public selectedFilter: VirtualFolder;
  public listState: ListState;
  // Same order and wording as the main section's list container, so the two read as one feature.
  public tabs: Tab[] = [
    { title: 'Folders', value: 'sheetFolders' },
    { title: 'Workbooks', value: 'sheets' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'sheets';

  /**
   * Derived pages (Flagged/Follow Up/Snoozed/Archive/Trash) offer no creation form — DP-001. Derived
   * from the stored page rather than from `selectFilter`, so it cannot drift from the list's filters.
   */
  protected readonly creationAllowed: Signal<boolean>;

  protected readonly popoverClose = new Subject<void>();
  private alive = new Subject<void>();
  /** Takes this panel's leave guard off the dynamic panel again. */
  private releaseLeaveGuard?: () => void;

  /**
   * SBUX-1043: the workbook open in the PANEL, rather than in the panel's form stack.
   *
   * The fourth panel is its own Sheets app, so opening a workbook keeps the collection heading above
   * it and leaves a named way back to the list it came from — the shape the Slides panel already uses
   * for a deck (`openedDeckId`). Routing it through `dpService.setFormItem` instead would replace the
   * whole panel body with the DP's form stack (`dynamic-panel.component.html`), taking the heading and
   * the list with it.
   */
  protected readonly openedWorkbook = signal<Sheet | null>(null);

  /**
   * SBUX-1043: the signal and what is remembered for the next mount, together — the panel is
   * destroyed and rebuilt on every app switch and every collapse, so a workbook it holds only in a
   * signal is gone by the time the user comes back. The handoff asks for the opposite: "do not
   * reinitialize its navigation state on every app switch".
   */
  private showWorkbook(sheet: Sheet | null): void {
    // A tab, a worksheet and a cell belong to the workbook that was showing them, so a change of
    // workbook — to another, or to none — hands the position being left back to its own workbook and
    // takes up the one the arriving workbook was last left on.
    if (sheet?.id !== this.openedWorkbook()?.id) {
      this.rememberCellOnTheWayOut();
      this.restorePosition(sheet);
    }
    this.setOpenedWorkbook(sheet);
    this.stateService.setOpenWorkbookId(sheet?.id ?? null);
  }

  private releaseWorkbookHold: (() => void) | null = null;
  private destroyed = false;

  /** The one write of `openedWorkbook`: a shown workbook is held synchronously, before its form mounts; none after destroy. */
  private setOpenedWorkbook(sheet: Sheet | null): void {
    if (sheet && this.destroyed) return;
    if (sheet && !this.releaseWorkbookHold) this.releaseWorkbookHold = this.dpService.holdPanelItem();
    this.openedWorkbook.set(sheet);
    if (!sheet) this.releaseHeldWorkbook();
  }

  private releaseHeldWorkbook(): void {
    this.releaseWorkbookHold?.();
    this.releaseWorkbookHold = null;
  }

  /**
   * Puts the three position signals back to where this workbook was last left, or to the defaults
   * for a workbook that has never been open here — and for none.
   *
   * It only SETS the signals: writing them back through `remember…` would store the arriving
   * workbook's position before it has one, under the departing workbook's id.
   */
  private restorePosition(sheet: Sheet | null): void {
    const kept = sheet ? this.stateService.getWorkbookContext(sheet.id) : null;
    this.openTab.set(kept?.tab ?? null);
    this.openSheetId.set(kept?.sheet ?? null);
    this.openCell.set(kept?.cell ?? null);
    this.cellNow = null;
  }

  /** Keeps part of the open workbook's position. Nothing open is nothing to remember it against. */
  private remember(position: WorkbookPosition): void {
    const id = this.openedWorkbook()?.id;
    if (id) this.stateService.setWorkbookContext(id, position);
  }

  /**
   * A workbook the panel itself made — "+ Use template", an import — replaces what is open here.
   *
   * Through `openSheet`, not `showWorkbook`: this is a swap like any other, so it asks the workbook
   * being replaced the same question the list's own rows ask it. Going straight to `showWorkbook`
   * took the editor away without asking, and an import is reachable (the filters menu) while a
   * workbook is open with a cell the grid has refused to save.
   */
  /**
   * What `PanelSheetCreateComponent` asks before it creates anything: the same guard every other
   * door out of an open workbook asks. Bound as a value so the input holds one stable reference
   * rather than a new closure per render.
   */
  /** For the header's Add menu, which hides the app's own type — workbooks have their own bar. */
  protected readonly Application = Application;

  protected readonly mayCreate = (): Promise<boolean> => this.mayLeaveWorkbook();

  protected openCreated(sheet: Sheet): void {
    this.openSheet(sheet);
  }

  /**
   * What the panel calls the collection it is showing: the section's own name for it, so the heading, the
   * dropdown and "Back to {view}" say the same word as the sidebar ("Workbooks" for the first one).
   */
  protected collectionTitleOf(folder: VirtualFolder): string {
    return this.collectionTitle.transform(folder);
  }

  /** The tab the open workbook is showing, for the next time this panel is built. */
  protected readonly openTab = signal<string | null>(null);

  protected rememberTab(tab: string | null): void {
    this.openTab.set(tab);
    this.remember({ tab });
  }

  /** The worksheet the open workbook is showing, for the next time this panel is built. */
  protected readonly openSheetId = signal<string | null>(null);

  protected rememberSheet(id: string | null): void {
    // Only a CHANGE of worksheet drops the cell. An editor publishes its worksheet once as it
    // mounts, and that one is the worksheet just restored — clearing on it erased the cell the
    // panel had been put back on, every single time it was built.
    const moved = id !== this.openSheetId();
    this.openSheetId.set(id);
    if (!moved) {
      this.remember({ sheet: id });
      return;
    }
    // The restored cell goes with the worksheet it was on. Leaving it set let the way out below fall
    // back to it and store the PREVIOUS worksheet's cell against the new one.
    this.cellNow = null;
    this.openCell.set(null);
    this.remember({ sheet: id, cell: null });
  }

  /** The cell to put the user back on, read from storage when a workbook is reopened. */
  protected readonly openCell = signal<string | null>(null);

  /**
   * SBUX-1043: where the user is in the grid, kept in memory as it changes and written to storage
   * only as the panel goes. Two things rule out the obvious alternatives: storing on every change
   * would write to localStorage per click, and ASKING the editor at `ngOnDestroy` returns null —
   * its handle is disposed by then, which the app showed plainly (the panel stored `null` on every
   * app switch while the same call a moment earlier returned "C5").
   */
  private cellNow: string | null = null;

  protected rememberCell(cell: string): void {
    this.cellNow = cell;
  }

  private readonly rememberCellOnTheWayOut = (): void => {
    if (!this.openedWorkbook()) return;
    // Falling back to what was RESTORED, for a panel left again before its editor reported a cell:
    // nothing moved, so the cell the user is on is still the one they were put back on.
    const cell = this.cellNow ?? this.openCell();
    // Neither means there is no cell to remember — a template, or a workbook opened and left again
    // before the user touched the grid. Writing the null anyway made an entry holding nothing, which
    // still counted against the positions kept and could push a real one out.
    if (!cell) return;
    this.remember({ cell });
  };

  /**
   * SBUX-1043: the folder being browsed, in the panel itself.
   *
   * Opening a folder used to hand it to `dpService.setFormItem` as well, which shows the folder's FORM
   * in the DP's stack — its workbooks are in there, but the panel's own heading and list are gone, and
   * the only way out is the DP's generic back arrow. Here the folder is a place the list is in: its
   * name heads the list, with the arrow back to Folders immediately left of it.
   */
  protected readonly openedFolder = signal<SheetFolder | null>(null);
  /** As a source, so the workbook filters are rebuilt when the folder being browsed changes. */
  private readonly openedFolder$ = toObservable(this.openedFolder);

  /** NFR-8: this panel's words come from the Sheets strings, like the rest of the app's. */
  protected readonly S = SHEETS_STRINGS;
  private readonly collectionTitle = new TitleBySheetsFilterPipe();
  private readonly host = inject(ElementRef<HTMLElement>);
  /** The open workbook's view, which is what can say whether its grid may be left. */
  private readonly workbookView = viewChild(SheetsPanelWorkbookComponent);
  private readonly formChanges = inject(FormChangesModalsService);
  private readonly splitView = inject(SplitViewService);

  /**
   * Where Back goes, named as the heading names it: the folder being browsed when there is one
   * ("Back to Finance"), else the collection ("Back to Sheets"). A method rather than a `computed`:
   * `selectedFilter` is a plain property the context menu writes, not a signal.
   */
  protected backLabel(): string {
    const folder = this.openedFolder();
    return fill('Back to {view}', { view: folder?.title || this.collectionTitleOf(this.selectedFilter) });
  }

  private readonly sheetsListStateService = inject(SheetsListStateService, { self: true });
  private readonly sheetFolderListStateService = inject(SheetFolderListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  /** What "Add details" needs to create a workbook and open it (new-workbook.ts). */
  private readonly sheetsService = inject(SheetsService);
  /** The app's toasts, which outlive this panel — an import's result arrives after it may be gone. */
  private readonly toaster = inject(ToasterService);
  private readonly sheetFoldersService = inject(SheetFoldersService);
  private readonly newWorkbook: NewWorkbookDeps = { sheetsService: this.sheetsService, router: inject(Router) };

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  /** Location badges are shown on sheet rows only while an advanced search is applied. */
  protected readonly isAdvancedSearchApplied: Signal<boolean>;
  /**
   * Search-shaped: the shared query/markers plus transient ids. Sheets exposes no form-only fields of its
   * own (`SheetsAppAdvancedFilters` is base-only), so there is nothing extra to report here; the date
   * range and `stitched` light the funnel pill instead.
   */
  protected readonly isSearchApplied: Signal<boolean>;

  constructor(
    private dpService: DynamicPanelService,
    private modalService: ModalService,
    private injector: Injector,
    private searchService: AdvancedSearchService,
    private stateService: SheetsAppStateService,
  ) {
    this.isAdvancedSearchApplied = toSignal(this.searchService.isAdvancedSearchApplied(Application.sheets), {
      initialValue: false,
    });
    this.isSearchApplied = toSignal(this.searchService.isSearchApplied(Application.sheets), { initialValue: false });
    this.creationAllowed = toSignal(
      this.stateService.getVirtualFolder().pipe(map((folder) => isCreationAllowedInVirtualFolder(folder))),
      { initialValue: true },
    );
  }

  public ngOnInit() {
    // SBUX-1043: the panel's app switcher and its collapse both destroy this component, and neither
    // has a route to guard it. Registering here asks the same two guards the panel's own Back asks,
    // so the cell being typed in is kept rather than going with the panel.
    //
    // `registerSurfaceGuard` is SV1-481's: the DP keeps a SET of these and asks all of them before
    // changing app, opening an item or collapsing. This branch grew its own single-slot version of
    // the same idea in parallel; main's is the more general of the two and is the one that survived
    // the rebase, so Sheets is a second registrant beside Slides' preview rather than a second
    // mechanism. It wants an Observable, hence `from`.
    //
    // `mayLeaveWorkbook`, NOT `leaveWorkbook`: the panel is about to be destroyed either way, and
    // CLOSING the workbook here would also forget it — the stored id is how the panel comes back to
    // it, and the user switching to Mail for a moment has not closed anything.
    //
    // `catchError` is the boundary's own backstop, not the fix: `mayLeaveWorkbook` already swallows
    // its two rejecting halves and resolves true (see there for why allowing beats refusing). This
    // is here because what is on the other side of it is SHARED — `prepareSurfaceLeave` runs every
    // registered guard through ONE `combineLatest`, so an error escaping this one would not merely
    // fail Sheets, it would error that stream, and changing app, opening an item and collapsing
    // would go silently dead for EVERY panel app. A promise handed across that boundary is worth a
    // catch even when today's implementation cannot reject; the next edit to `mayLeaveWorkbook`
    // should not be able to break Mail.
    this.releaseLeaveGuard = this.dpService.registerSurfaceGuard(() =>
      from(this.mayLeaveWorkbook()).pipe(catchError(() => of(true))),
    );
    // NOT while the panel is collapsed. The DP re-creates this component inside its 15px strip, and
    // restoring the workbook THERE costs a full re-open over the network — the item, its snapshot,
    // stitches, comments, tags, knots — and a `POST .../sessions` that registers a server-side
    // editing session for a workbook nobody can see. It is also where the restored cell's bad scroll
    // offset comes from: the engine is built one column wide, so selecting the cell scrolls it to the
    // origin, and the offset survives the panel being widened again. The instance built when the
    // panel is expanded is the one that should restore it.
    if (!this.splitView.isMinimized(DP_SPLIT_KEY)) this.reopenWhereItLeftOff();
    // `ngOnDestroy` does not run when the page itself goes, so a reload would otherwise keep
    // whatever was stored at the last app switch. `pagehide` fires for a reload, a close and a
    // navigation away alike.
    window.addEventListener('pagehide', this.rememberCellOnTheWayOut);

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
      });

    combineLatest([
      this.sheetFolderListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.sheetFoldersFilters = new SheetFoldersFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.sheetsListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.openedFolder$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone, browsing]) => {
        this.sheetsFilters = new SheetsFilters({
          displayTimeZone,
          // SBUX-1043: inside a folder the list is that folder's workbooks, as its own form lists them.
          ...(browsing ? { containersIds: [browsing.id] } : {}),
        })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  /**
   * SBUX-1043: back to the workbook and the folder the panel had open when it was last destroyed —
   * an app switch, a collapse, or a page load. The ids are remembered (`SheetsAppStateService`); the
   * ITEMS are re-read here, because a workbook shown from a remembered copy would be as it was
   * before the switch, and the panel's editor would then save that over whatever happened since.
   *
   * Anything that fails is simply forgotten: a workbook that has been deleted for good, or a folder
   * the user can no longer see, leaves the panel on its list rather than on an error.
   */
  private reopenWhereItLeftOff(): void {
    const folderId = this.stateService.getOpenFolderId();
    if (folderId) {
      this.sheetFoldersService
        .getItem(folderId)
        .pipe(take(1), takeUntil(this.alive))
        .subscribe({
          next: (folder) => folder && this.openedFolder.set(folder),
          error: () => this.stateService.setOpenFolderId(null),
        });
    }

    const workbookId = this.stateService.getOpenWorkbookId();
    if (!workbookId) return;
    this.sheetsService
      .getItem(workbookId)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe({
        // Not `showWorkbook`: the id is already stored, and re-storing it here would be the only
        // write on a path that is meant to be a read. The position is restored the same way it is,
        // which is the one thing this is here to bring back.
        next: (sheet) => {
          if (!sheet) return;
          this.restorePosition(sheet);
          this.setOpenedWorkbook(sheet);
        },
        error: () => this.stateService.setOpenWorkbookId(null),
      });
  }

  public ngOnDestroy() {
    this.destroyed = true;
    window.removeEventListener('pagehide', this.rememberCellOnTheWayOut);
    this.rememberCellOnTheWayOut();
    this.releaseLeaveGuard?.();
    this.releaseHeldWorkbook();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  /**
   * "Add details" on the panel's "Add New Workbook" form (gap row 37, as decided for the panel on 2026-10-07):
   * the workbook is created as the form has it and opened, as New Workbook and the library's other
   * "Add New Workbook" forms do. It opened the unsaved draft in the full form, the flow Wook rejected on
   * 2026-10-06.
   *
   * SBUX-1043: opened HERE. It used to navigate to the editor's route, which took the main app off whatever
   * page it was on because a workbook made in a side panel needed somewhere to go. The panel has an editor
   * of its own now, so the workbook opens where it was made and the main app is left alone.
   */
  handleNewSheet(sheet = new Sheet()) {
    // Made while browsing a folder, it is filed there — the destination the form itself names wins.
    const browsing = this.openedFolder();
    if (browsing && !sheet.sheetFolderId) sheet.sheetFolderId = browsing.id;
    void createAndOpenDraft(this.newWorkbook, sheet, { open: (created) => this.showWorkbook(created) });
  }

  /** "Import spreadsheet", the panel's own (handoff, 7 October). */
  protected readonly importOpen = signal(false);
  protected readonly importBusy = signal(false);

  /**
   * The file becomes a workbook and opens HERE, not in the main app — as everything else the panel
   * creates does. The browsed folder is read BEFORE the parse is awaited, which is what the handoff
   * asks for ("import captures its folder before parsing"): a large file takes long enough for the
   * user to leave the folder, and the workbook belongs where it was started.
   */
  protected async importChosen(file: File): Promise<void> {
    if (this.importBusy()) return;
    const sheetFolderId = this.openedFolder()?.id ?? null;
    this.importBusy.set(true);
    const opened = await importAndOpenWorkbook(
      { sheetsService: this.sheetsService, router: this.newWorkbook.router, toaster: this.toaster },
      file,
      { sheetFolderId, open: (created) => this.openCreated(created) },
    );
    this.importBusy.set(false);
    // It stays open on a failure, for another try; the toast has already said why.
    if (opened) this.importOpen.set(false);
  }

  openSheetInModal(sheet: Sheet) {
    this.modalService.openFullForm(sheet, this.injector);
  }

  /**
   * SBUX-1043: a workbook opens in the panel itself, over its own list.
   *
   * It used to go to `dpService.setFormItem`, on the reasoning that the panel cannot hold a workbook
   * because EXPANDING one takes columns 2 and 3 of the main layout. That reasoning stands for the
   * expanded editor and is why "Open" in the editor header still leaves the panel — but the panel's
   * own workbook view is panel-width, so it does not apply to this.
   *
   * The list stays mounted underneath (see the template): its scroll position, and the page it has
   * loaded, are the list's own DOM, and returning to a list that was rebuilt from scratch would put
   * the user back at the top of it.
   */
  openSheet(sheet: Sheet) {
    const open = this.openedWorkbook();
    // Swapping one open workbook for ANOTHER is leaving the first, so it asks the first's guards —
    // the same two every other door asks. Reachable without going through the list: trashing the open
    // workbook moves the list's selection to the next survivor and opens it, and "+ Use template"
    // opens what it made. See `SheetsPanelWorkbookComponent` for why the editor is keyed as well.
    if (open && open.id !== sheet.id) {
      void this.mayLeaveWorkbook().then((mayLeave) => mayLeave && this.showWorkbook(sheet));
      return;
    }
    this.showWorkbook(sheet);
  }

  /**
   * SBUX-1043: "Open in main app" — the one thing the handoff says the panel must be able to do with
   * its workbook that it otherwise cannot.
   *
   * The panel KEEPS the workbook open: this changes the main app's selection, not the panel's, which
   * is the whole point of two independent Sheets apps ("navigation does not follow the other view").
   * The worksheet goes with it, so the full editor opens where the panel was rather than on the
   * workbook's first sheet. The grid was already flushed by the view's own guard.
   */
  protected openInMainApp(): void {
    const open = this.openedWorkbook();
    if (!open) return;
    void openWorkbookInEditor(this.newWorkbook.router, open.id, this.openSheetId() ?? undefined);
  }

  /**
   * Back to the list, and Close workbook: the same return, from the two controls the panel offers for
   * it. Both pass through the unsaved-changes guard first, as the DP's own form stack does
   * (`FormsContainerComponent.handleBack`), so a half-typed title is not thrown away silently.
   *
   * The app bar's own X is NOT this: that closes the whole Sheets panel and is the DP's.
   */
  protected closeWorkbook(): void {
    void this.leaveWorkbook();
  }

  /**
   * Closes the open workbook if both guards allow it, and answers whether it did.
   *
   * The GRID goes first: it keeps whatever cell is being typed in, and refuses only for work it
   * cannot save at all — so the ordinary close is silent. Then the form's own dirty fields. Asking
   * the form alone, as this did at first, threw away a half-typed cell on Back, which the Sheets
   * page's editor has never allowed (its route guard asks the grid the same question).
   *
   * The answer matters to `selectFilter`: a guard that says "stay" must keep the panel where it is,
   * heading included.
   */
  private async leaveWorkbook(): Promise<boolean> {
    if (!this.openedWorkbook()) return true;
    if (!(await this.mayLeaveWorkbook())) return false;
    this.showWorkbook(null);
    this.focusList();
    return true;
  }

  /**
   * The two guards alone, without closing: what a swap from one open workbook to another asks, where
   * the panel goes on showing a workbook and neither the list nor the focus should move.
   *
   * **It never rejects, and that is the point.** Every door out of an open workbook funnels through
   * here — Back, the header's X, a swap to another row, changing collection, and the DP's own leave
   * guard — and all but the last reach it as a bare `void …then(…)`. A rejection would therefore not
   * surface anywhere: the `then` would not run, no toast, no console, and clicking Back or another
   * workbook would simply do nothing, for ever, with no way out of the panel's workbook view.
   *
   * Both halves can reject. `canLeave()` flushes the engine, and `firstValueFrom` rejects on a
   * stream that errors or completes without emitting.
   *
   * Allowing is the right answer rather than refusing: refusing on an error we could not interpret
   * would wedge the panel shut, where allowing costs at most the cell being typed in — and the save
   * machine autosaves independently of this path, so "at most" is usually "nothing". The panel also
   * remembers the workbook, so the user lands back on it.
   */
  private async mayLeaveWorkbook(): Promise<boolean> {
    const open = this.openedWorkbook();
    if (!open) return true;
    try {
      if (!(await (this.workbookView()?.canLeave() ?? Promise.resolve(true)))) return false;
      return await firstValueFrom(this.formChanges.confirmUnsavedChanges(open.id));
    } catch {
      return true;
    }
  }

  /**
   * Where focus goes on the way back. The handoff asks for the workbook's row, or the list when that
   * row is gone; our rows are not focusable — `appSelectableList` puts the tabindex on the scrolling
   * region (`role="region"`, labelled "Workbooks"/"Folders") and the rows carry none — so the list is
   * both the fallback and the only target there is. Making rows focusable is a change to the
   * selectable-list directive every app's list uses.
   */
  private focusList(): void {
    // After the workbook view has gone and the list is interactive again.
    setTimeout(() => (this.host.nativeElement as HTMLElement).querySelector<HTMLElement>('.panel-list [role="region"]')?.focus());
  }

  /**
   * SBUX-1043: a folder is browsed in the panel — its workbooks become the list, under its name.
   * Its own form is still a right-click away ("Open in Modal", "Settings"), as it is for a workbook.
   */
  openSheetFolder(sheetFolder: SheetFolder) {
    this.openedFolder.set(sheetFolder);
    this.stateService.setOpenFolderId(sheetFolder?.id ?? null);
  }

  /**
   * Which of the two lists shows. Inside a folder it is always the workbooks — the folder IS the list.
   * Derived rather than assigned to `selectedTab`: the app state drives that property directly
   * (`getTabs()` in ngOnInit), so an assignment here would be undone the next time it emits.
   */
  protected listTab(): Exclude<ListState, 'tabs'> {
    return this.openedFolder() ? 'sheets' : this.selectedTab;
  }

  /**
   * The arrow left of the folder's name: back to the folder list, with the workbook query cleared.
   *
   * It waits for the guard the same way `selectFilter` does. Firing the close and clearing the
   * folder anyway left the two disagreeing when a guard said stay — the panel went on showing the
   * workbook while the heading above it had already gone back to Folders.
   */
  protected leaveFolder(): void {
    void this.leaveWorkbook().then((left) => {
      if (!left) return;
      this.openedFolder.set(null);
      this.stateService.setOpenFolderId(null);
      this.selectedTab = 'sheetFolders';
    });
  }

  /**
   * Picking a collection shows that collection's list, so a workbook open over it is closed first —
   * otherwise the heading names one list while the panel shows a workbook from another.
   *
   * The guard runs inside `leaveWorkbook`, and a refusal keeps **everything** where it is, the
   * collection included. An earlier version changed the collection anyway, on the reasoning that the
   * user had asked for that list; it left the heading naming one list above a workbook from another,
   * which is the state this method exists to prevent. `leaveFolder` waits the same way.
   */
  selectFilter(folder: VirtualFolder): void {
    void this.leaveWorkbook().then((left) => {
      // A guard said stay: the panel stays where it is, heading included. Changing the collection
      // anyway left it naming one list over a workbook from another.
      if (!left) return;
      // A collection is a different list, so the folder being browsed inside the old one is left too.
      this.openedFolder.set(null);
      this.stateService.setOpenFolderId(null);
      this.selectedFilter = folder;
      this.stateService.setVirtualFolder(folder);
    });
  }

  protected handleSearch(): void {
    this.popoverClose.next();
  }

  protected clearSearch(): void {
    // Only the search terms: the per-app date/stitched filters belong to the filters menu and its pill.
    this.searchService.resetSearchTerms();
    // The transient "View Contacts" ids are part of those terms, so the mode they opened ends here —
    // at the owner, independently of whether the folder-menu directive is mounted to notice.
    this.dpService.clearIdsSearchMode();
  }

  protected handleCancel(): void {
    this.popoverClose.next();
  }

  /** "Add details" on the quick form: continue in the full form with what was typed. */
  protected handleNewSheetFolder(sheetFolder: Stitch): void {
    this.modalService.openFullForm(sheetFolder, this.injector);
  }
}
